import { jsx as _jsx } from "react/jsx-runtime";
import { BoilerAdapter, RadioSensor, } from 'z3k-config';
import { useZ3KBoilerAdapterState, useZ3KDeviceState, useZ3KObjectState, } from 'console/shared/hooks/device/z3k';
import { memo, useMemo } from 'react';
import { ThermometerContent } from './types';
import { useDeviceConfig } from 'console/shared/contexts/device/config';
import { Z3kSecondRoutes } from 'z3k-settings/routes/type';
import { ThermometerState } from 'webui/utils/thermometers';
import { get_thermometer_rf, get_thermometer_state, temperature_from_state, } from 'z3k-parser/lib';
export const Z3KThermometerProvider = memo(function Z3KThermometerProvider(props) {
    const { children, thermometer_config } = props;
    const { z3k_config } = useDeviceConfig();
    const { id, thermometer_key, owa, open_therm } = thermometer_config;
    const z3k_state = useZ3KDeviceState();
    const { ot: ot_state } = useZ3KBoilerAdapterState(open_therm?.boiler_adapter_id);
    const z3k_thermometer_state = useZ3KObjectState(id);
    const z3k_heating_circuit_state = useZ3KObjectState(open_therm?.heating_circuit_id);
    const boiler_adapter_state = useMemo(() => {
        if (open_therm && z3k_thermometer_state) {
            const { boiler_adapter_id } = open_therm;
            const parent_boiler_adapter = BoilerAdapter.get.byId(z3k_config, boiler_adapter_id);
            const { firmware } = z3k_config.device;
            return parent_boiler_adapter
                ? BoilerAdapter.getSensorErrorState(parent_boiler_adapter, z3k_state, parseInt(firmware, 10))
                : ThermometerState.disconnected;
        }
        return ThermometerState.disconnected;
    }, [
        z3k_config,
        open_therm?.boiler_adapter_id,
        z3k_heating_circuit_state,
        z3k_state,
    ]);
    const boiler_adapter_temperature = useMemo(() => {
        if (open_therm) {
            const { ot_name } = open_therm;
            const temperature = ot_state?.[ot_name];
            return temperature;
        }
        return undefined;
    }, [ot_state, open_therm?.ot_name]);
    const is_radio_sensor_no_connect = useMemo(() => {
        if (thermometer_key === Z3kSecondRoutes.radiosensor868) {
            return RadioSensor.isNoConnect(z3k_thermometer_state);
        }
        return undefined;
    }, [z3k_thermometer_state, thermometer_key]);
    const state = useMemo(() => get_thermometer_state(z3k_thermometer_state, is_radio_sensor_no_connect), [z3k_thermometer_state, is_radio_sensor_no_connect]);
    const temperature = useMemo(() => temperature_from_state(z3k_thermometer_state), [z3k_thermometer_state]);
    const rf_indicators = useMemo(() => get_thermometer_rf(z3k_thermometer_state), [z3k_thermometer_state]);
    const thermometer_state = useMemo(() => {
        if (thermometer_key === Z3kSecondRoutes.wired_temperature_sensors && owa) {
            const { multiplier } = owa;
            const owa_value = temperature != null ? temperature * (multiplier ?? 1) : undefined;
            return { state, temperature: owa_value };
        }
        if (thermometer_key === Z3kSecondRoutes.radiosensor868) {
            return { state, temperature, rf_indicators };
        }
        if (thermometer_key === Z3kSecondRoutes.boiler_adapters) {
            return {
                state: boiler_adapter_state,
                temperature: boiler_adapter_temperature,
            };
        }
        return { state, temperature };
    }, [state, temperature, rf_indicators, owa, boiler_adapter_temperature]);
    const thermometer = useMemo(() => ({
        ...thermometer_state,
        ...thermometer_config,
    }), [thermometer_config, thermometer_state]);
    return (_jsx(ThermometerContent.Provider, { value: thermometer, children: children }));
});
